import { useEffect, useState } from "react";
import axios from "axios";
import { API, getAuthHeaders, useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Radio, Copy, RefreshCw, Activity, Globe, AlertTriangle, CheckCircle2, X, Clock } from "lucide-react";

/** Admin "Signal Dispatch" page (replaces the old MetaApi Bridge Master).
 *  Surfaces:
 *    • TradingView webhook URL + payload templates (copy buttons)
 *    • Rotate webhook secret (master-only)
 *    • Recent signals table with delivery counts
 *    • Click row → modal with per-follower delivery breakdown */
export default function SignalDispatch() {
  const { user } = useAuth();
  const isMaster = user?.role === "master_admin";
  const [cfg, setCfg] = useState(null);
  const [signals, setSignals] = useState([]);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState("");
  const [publicUrl, setPublicUrl] = useState("");
  // Master lot — controls what size the master account trades. Two knobs:
  //   • lotValue: default fallback lot used when TV payload omits `lot`.
  //   • lotOverride: when true, EVERY TV signal is forced to lotValue,
  //     ignoring whatever TradingView sends.
  const [lotValue, setLotValue] = useState(0.01);
  const [lotOverride, setLotOverride] = useState(false);

  const refresh = async () => {
    try {
      const [c, s] = await Promise.all([
        axios.get(`${API}/admin/signals/webhook-config`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/signals/recent?limit=25`, { headers: getAuthHeaders() }),
      ]);
      setCfg(c.data);
      setSignals(s.data?.rows || []);
      if (!publicUrl) setPublicUrl(c.data?.public_base_url || "");
      // Sync master-lot form state with server config (only on initial load
      // or after external rotate — respect in-progress user edits).
      if (c.data && lotValue === 0.01 && !lotOverride) {
        setLotValue(Number(c.data.master_lot_default ?? 0.01));
        setLotOverride(Boolean(c.data.master_lot_override));
      }
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 8000);
    return () => clearInterval(id);
     
  }, []);

  const copyTxt = (txt, label) => {
    navigator.clipboard.writeText(txt).then(() => toast.success(`${label} copied`));
  };

  const rotateSecret = async () => {
    if (!window.confirm("Rotate the webhook secret?\n\nThe current TradingView webhook URL will stop working immediately — you'll need to paste the new URL into TradingView.")) return;
    setBusy("rotate");
    try {
      await axios.post(`${API}/admin/signals/rotate-secret`, {}, { headers: getAuthHeaders() });
      toast.success("Webhook secret rotated");
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  const savePublic = async () => {
    setBusy("public");
    try {
      await axios.put(`${API}/admin/signals/public-url`, { url: publicUrl }, { headers: getAuthHeaders() });
      toast.success("Public URL saved");
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  const saveMasterLot = async () => {
    const v = parseFloat(lotValue);
    if (!(v >= 0.01 && v <= 100)) {
      toast.error("Master lot must be between 0.01 and 100");
      return;
    }
    setBusy("master-lot");
    try {
      await axios.put(
        `${API}/admin/signals/master-lot`,
        { master_lot_default: v, master_lot_override: lotOverride },
        { headers: getAuthHeaders() },
      );
      toast.success(lotOverride ? `Master lot LOCKED at ${v}` : `Default master lot set to ${v}`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update master lot");
    } finally {
      setBusy("");
    }
  };

  const openDetail = async (sigId) => {
    try {
      const r = await axios.get(`${API}/admin/signals/${sigId}`, { headers: getAuthHeaders() });
      setDetail(r.data);
    } catch (err) { toast.error("Failed to load detail"); }
  };

  const fullUrl = cfg?.full_webhook_url || (publicUrl ? `${publicUrl.replace(/\/$/, "")}${cfg?.webhook_url_path || ""}` : cfg?.webhook_url_path);

  return (
    <div className="space-y-6" data-testid="signal-dispatch-section">
      {/* Webhook config */}
      <div className="p-5 rounded-xl border border-white/10 bg-[#13131A]/60" data-testid="webhook-config-card">
        <div className="flex items-center gap-2 mb-3">
          <Radio size={14} className="text-[#7DD3FC]" />
          <h3 className="text-sm font-bold text-white">TradingView Webhook</h3>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1 block flex items-center gap-1">
              <Globe size={10} /> Public Server URL
            </label>
            <div className="flex gap-2">
              <input
                data-testid="public-url-input"
                value={publicUrl}
                onChange={(e) => setPublicUrl(e.target.value)}
                disabled={!isMaster}
                placeholder="https://api.veloxalgotech.com"
                className="flex-1 px-3 py-2 rounded-md bg-zinc-950 border border-zinc-800 text-white text-sm font-mono outline-none focus:border-[#7DD3FC]/50 disabled:opacity-40"
              />
              {isMaster && (
                <button
                  data-testid="public-url-save"
                  onClick={savePublic}
                  disabled={busy === "public"}
                  className="px-3 py-2 rounded-md text-xs font-semibold border border-[#7DD3FC]/40 text-[#7DD3FC] hover:bg-[#7DD3FC]/10 disabled:opacity-40"
                >
                  {busy === "public" ? "Saving…" : "Save"}
                </button>
              )}
            </div>
            <p className="text-[10px] text-zinc-500 mt-1">This is the public origin we&apos;ll prepend to the webhook path. Set once.</p>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1 block">TradingView Webhook URL</label>
            <div className="flex gap-2">
              <input
                data-testid="webhook-url"
                readOnly
                value={fullUrl || ""}
                className="flex-1 px-3 py-2 rounded-md bg-zinc-950 border border-zinc-800 text-white text-xs font-mono outline-none"
              />
              <button
                data-testid="webhook-url-copy"
                onClick={() => copyTxt(fullUrl, "Webhook URL")}
                className="px-3 py-2 rounded-md text-xs font-semibold border border-white/10 text-zinc-300 hover:border-white/30"
              ><Copy size={11} /></button>
              {isMaster && (
                <button
                  data-testid="webhook-rotate"
                  onClick={rotateSecret}
                  disabled={busy === "rotate"}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-xs font-semibold border border-rose-500/30 text-rose-300 hover:bg-rose-500/10 disabled:opacity-40"
                >
                  <RefreshCw size={11} className={busy === "rotate" ? "animate-spin" : ""} /> Rotate
                </button>
              )}
            </div>
          </div>

          <PayloadBlock title="OPEN payload (TradingView Alert message)" payload={cfg?.payload_template} onCopy={copyTxt} testid="payload-open" />
          <PayloadBlock title="CLOSE / partial-close payload" payload={cfg?.close_template} onCopy={copyTxt} testid="payload-close" />

          {/* Master-lot control (master-admin only) */}
          {isMaster && (
            <div
              className="mt-4 p-4 rounded-lg border border-white/10 bg-black/30"
              data-testid="master-lot-panel"
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="text-xs font-bold text-white flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    Master Trade Size
                  </h3>
                  <p className="text-[10px] text-zinc-500 mt-0.5">
                    Sets the lot size the master account executes on every TradingView signal.
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
                <div className="flex-1 w-full">
                  <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 mb-1 block">
                    Master lot (0.01 – 100)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max="100"
                    value={lotValue}
                    onChange={(e) => setLotValue(e.target.value)}
                    data-testid="master-lot-input"
                    className="w-full px-3 py-2 rounded-md bg-black/50 border border-white/10 text-sm font-mono text-white focus:outline-none focus:border-[#00b4d8]"
                    placeholder="0.01"
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer select-none pb-1.5">
                  <input
                    type="checkbox"
                    checked={lotOverride}
                    onChange={(e) => setLotOverride(e.target.checked)}
                    data-testid="master-lot-override-checkbox"
                    className="w-4 h-4 accent-amber-400"
                  />
                  <span className="text-[11px] text-zinc-300">
                    <b>Override:</b> ignore payload lot, always use this value
                  </span>
                </label>
                <button
                  onClick={saveMasterLot}
                  disabled={busy === "master-lot"}
                  data-testid="master-lot-save-btn"
                  className="px-4 py-2 rounded-md text-xs font-bold bg-[#00b4d8] text-[#0b132b] hover:bg-[#0077b6] hover:text-white disabled:opacity-40"
                >
                  {busy === "master-lot" ? "Saving…" : "Save Lot"}
                </button>
              </div>
              <p className="text-[10px] text-zinc-500 leading-relaxed mt-2">
                {lotOverride ? (
                  <><b className="text-amber-300">Override ON</b> — every TV signal executes at <b className="font-mono text-white">{lotValue}</b> lots on master, regardless of what TradingView emits. Followers still scale based on their own lot_mode.</>
                ) : (
                  <>TV payloads that include a <code className="text-white/70">lot</code> value use it as-is. When missing, master falls back to <b className="font-mono text-white">{lotValue}</b> lots.</>
                )}
              </p>
            </div>
          )}
        </div>

        <p className="mt-4 text-[10px] text-zinc-500 leading-relaxed">
          📌 On TradingView: open your Strategy → Alerts → Webhook URL field, paste the URL above, and set the &quot;Message&quot; body to one of the JSON payloads. Use TradingView&apos;s template variables (<span className="font-mono">{"{{strategy.order.action}}"}</span>, <span className="font-mono">{"{{strategy.order.contracts}}"}</span>) as needed.
        </p>
      </div>

      {/* Recent signals */}
      <div className="p-5 rounded-xl border border-white/10 bg-[#13131A]/60" data-testid="recent-signals-card">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-bold text-white flex items-center gap-2">
            <Activity size={12} className="text-[#7DD3FC]" /> Recent Signals
          </h4>
          <span className="text-[10px] text-zinc-500">{signals.length} shown · refresh every 8s</span>
        </div>
        <div className="rounded-lg border border-white/5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-white/10">
              <tr>
                <th className="px-3 py-2 text-left">Time</th>
                <th className="px-3 py-2 text-left">Action</th>
                <th className="px-3 py-2 text-left">Symbol</th>
                <th className="px-3 py-2 text-right">Master Lot</th>
                <th className="px-3 py-2 text-right">SL / TP</th>
                <th className="px-3 py-2 text-right">Delivery</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {signals.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-[11px] text-zinc-500">No signals yet — fire one from TradingView.</td></tr>
              )}
              {signals.map(s => (
                <tr key={s.signal_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`signal-row-${s.signal_id}`}>
                  <td className="px-3 py-2 text-[10px] text-zinc-400 font-mono">{(s.created_at || "").slice(11, 19)}</td>
                  <td className="px-3 py-2 text-[10px] font-semibold uppercase">
                    {s.action === "open" ? (
                      <span className={s.side === "buy" ? "text-emerald-400" : "text-rose-400"}>{s.side || "open"}</span>
                    ) : (
                      <span className="text-amber-300">close{s.close_pct < 100 ? ` ${s.close_pct}%` : ""}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[11px] font-mono text-white">{s.symbol || "—"}</td>
                  <td className="px-3 py-2 text-right text-[11px] font-mono text-zinc-300">{s.master_lot?.toFixed(2) || "—"}</td>
                  <td className="px-3 py-2 text-right text-[10px] text-zinc-500 font-mono">{s.sl || "—"} / {s.tp || "—"}</td>
                  <td className="px-3 py-2 text-right text-[10px]">
                    <span className="text-emerald-400">{s.executed}</span>
                    <span className="text-zinc-600 mx-1">/</span>
                    <span className="text-sky-400">{s.delivered}</span>
                    <span className="text-zinc-600 mx-1">/</span>
                    <span className="text-amber-400">{s.pending}</span>
                    <span className="text-zinc-600 mx-1">/</span>
                    <span className="text-rose-400">{s.failed}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      data-testid={`signal-detail-${s.signal_id}`}
                      onClick={() => openDetail(s.signal_id)}
                      className="text-[10px] text-[#7DD3FC] hover:underline"
                    >Detail</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-[10px] text-zinc-500">Delivery counts: <span className="text-emerald-400">executed</span> / <span className="text-sky-400">delivered</span> (sent to EA) / <span className="text-amber-400">pending</span> / <span className="text-rose-400">failed</span></p>
      </div>

      {/* Detail modal */}
      {detail && <DetailModal d={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function PayloadBlock({ title, payload, onCopy, testid }) {
  if (!payload) return null;
  const txt = JSON.stringify(payload, null, 2);
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1 block">{title}</label>
      <div className="relative">
        <pre data-testid={testid} className="p-3 rounded-md bg-zinc-950 border border-zinc-800 text-[10px] text-zinc-300 font-mono overflow-x-auto">{txt}</pre>
        <button
          data-testid={`${testid}-copy`}
          onClick={() => onCopy(txt, title)}
          className="absolute top-2 right-2 px-2 py-1 rounded text-[10px] text-zinc-400 hover:text-white bg-zinc-900/80 border border-zinc-800"
        ><Copy size={10} /></button>
      </div>
    </div>
  );
}

function DetailModal({ d, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#13131A] border border-white/10 rounded-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="signal-detail-modal">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Signal</p>
            <p className="text-sm font-mono text-white">{d.signal_id}</p>
          </div>
          <button onClick={onClose} data-testid="signal-detail-close" className="p-1 rounded hover:bg-white/5"><X size={14} className="text-zinc-400" /></button>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-[11px]">
            <Stat label="Action" value={`${d.action} ${d.side || ""}`.trim()} />
            <Stat label="Symbol" value={d.symbol} mono />
            <Stat label="Master Lot" value={d.master_lot} mono />
            <Stat label="SL / TP" value={`${d.sl || "—"} / ${d.tp || "—"}`} mono />
          </div>
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-white/10">
              <tr>
                <th className="px-2 py-1.5 text-left">Affiliate</th>
                <th className="px-2 py-1.5 text-left">MT5</th>
                <th className="px-2 py-1.5 text-left">Status</th>
                <th className="px-2 py-1.5 text-right">Lot</th>
                <th className="px-2 py-1.5 text-left">Ticket</th>
                <th className="px-2 py-1.5 text-left">Error</th>
              </tr>
            </thead>
            <tbody>
              {(d.deliveries || []).map((x, i) => (
                <tr key={i} className="border-b border-white/5">
                  <td className="px-2 py-1.5 text-[11px]">
                    <div className="text-white">{x.name || "—"}</div>
                    <div className="text-[9px] text-zinc-500">{x.display_id} · {x.email}</div>
                  </td>
                  <td className="px-2 py-1.5 text-[10px] font-mono text-zinc-300">#{x.mt5_login}</td>
                  <td className="px-2 py-1.5">
                    <StatusBadge s={x.status} />
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-[10px] text-zinc-300">{x.lot_assigned ?? "—"}</td>
                  <td className="px-2 py-1.5 font-mono text-[10px] text-zinc-400">{x.mt5_ticket || "—"}</td>
                  <td className="px-2 py-1.5 text-[10px] text-rose-400 max-w-[200px] truncate" title={x.error || ""}>{x.error || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-[0.15em] text-zinc-500">{label}</p>
      <p className={`text-xs text-white ${mono ? "font-mono" : ""}`}>{value ?? "—"}</p>
    </div>
  );
}

function StatusBadge({ s }) {
  const map = {
    executed: { c: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300", i: <CheckCircle2 size={9} /> },
    delivered: { c: "bg-sky-500/10 border-sky-500/30 text-sky-300", i: <Clock size={9} /> },
    pending: { c: "bg-amber-500/10 border-amber-500/30 text-amber-300", i: <Clock size={9} /> },
    failed: { c: "bg-rose-500/10 border-rose-500/30 text-rose-300", i: <AlertTriangle size={9} /> },
  };
  const m = map[s] || { c: "bg-zinc-500/10 border-zinc-500/30 text-zinc-400", i: null };
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] font-semibold uppercase ${m.c}`}>
      {m.i}{s}
    </span>
  );
}
